import type { Icon } from '@examdojo/core/icon';
import type { StatTestType } from './test-library-datasets.model';
import { TEST_TYPE_GROUPS } from './test-library-datasets.model';

// ---------------------------------------------------------------------------
// Test type toggle options (top-level selector)
// ---------------------------------------------------------------------------

export interface TestTypeToggle {
  readonly type: StatTestType;
  readonly label: string;
}

export const TEST_TYPE_TOGGLES: readonly TestTypeToggle[] = [
  { type: 'one-sample-t', label: '1-Sample t' },
  { type: 'two-sample-t', label: '2-Sample t' },
  { type: 'paired-t', label: 'Paired t' },
  { type: 'chi-squared-independence', label: '\u03C7\u00B2 Indep.' },
  { type: 'chi-squared-gof', label: '\u03C7\u00B2 GOF' },
  { type: 'sign-test', label: 'Sign' },
];

// ---------------------------------------------------------------------------
// Test selector card items (card-based selector)
// ---------------------------------------------------------------------------

export interface TestSelectorItem {
  readonly type: StatTestType;
  readonly label: string;
  readonly description: string;
  readonly icon: Icon;
  readonly columnCount: number;
}

export const TEST_SELECTOR_ITEMS: Readonly<Record<StatTestType, TestSelectorItem>> = {
  'one-sample-t': {
    type: 'one-sample-t',
    label: 'One-Sample t-Test',
    description: 'Compare sample mean to a known value',
    icon: 'chart-simple',
    columnCount: 1,
  },
  'two-sample-t': {
    type: 'two-sample-t',
    label: 'Two-Sample t-Test',
    description: 'Compare means of two groups',
    icon: 'chart-column',
    columnCount: 2,
  },
  'paired-t': {
    type: 'paired-t',
    label: 'Paired t-Test',
    description: 'Compare two related measurements',
    icon: 'link',
    columnCount: 2,
  },
  'chi-squared-independence': {
    type: 'chi-squared-independence',
    label: '\u03C7\u00B2 Independence',
    description: 'Test if two categories are related',
    icon: 'table-cells',
    columnCount: 2,
  },
  'chi-squared-gof': {
    type: 'chi-squared-gof',
    label: '\u03C7\u00B2 Goodness of Fit',
    description: 'Test if distribution matches expected',
    icon: 'chart-pie',
    columnCount: 1,
  },
  'sign-test': {
    type: 'sign-test',
    label: 'Sign Test',
    description: 'Non-parametric paired comparison',
    icon: 'plus-minus',
    columnCount: 2,
  },
};

export interface TestSelectorGroup {
  readonly label: string;
  readonly items: readonly TestSelectorItem[];
}

export const TEST_SELECTOR_GROUPS: readonly TestSelectorGroup[] = TEST_TYPE_GROUPS.map((group) => ({
  label: group.label,
  items: group.testTypes.map((type) => TEST_SELECTOR_ITEMS[type]),
}));

// ---------------------------------------------------------------------------
// Test type titles (for the generator panel header)
// ---------------------------------------------------------------------------

export const TEST_TYPE_TITLE_LABELS: Readonly<Record<StatTestType, string>> = {
  'one-sample-t': 'One-Sample $t$-Test',
  'two-sample-t': 'Two-Sample $t$-Test',
  'paired-t': 'Paired $t$-Test',
  'chi-squared-independence': '$\\chi^2$ Test for Independence',
  'chi-squared-gof': '$\\chi^2$ Goodness of Fit Test',
  'sign-test': 'Sign Test',
};

// ---------------------------------------------------------------------------
// Short descriptions (one sentence, no hypothesis or formula)
// ---------------------------------------------------------------------------

export const TEST_SHORT_DESCRIPTIONS: Readonly<Record<StatTestType, string>> = {
  'one-sample-t':
    'Tests whether the mean of a single sample differs significantly from a hypothesized population mean $\\mu_0$.',
  'two-sample-t': 'Tests whether the means of two independent groups are significantly different.',
  'paired-t': 'Tests whether the mean difference between paired observations (e.g. before/after) is zero.',
  'chi-squared-independence':
    'Tests whether two categorical variables are independent, using a contingency table of observed frequencies.',
  'chi-squared-gof':
    'Tests whether observed frequencies match an expected distribution (e.g. uniform, given proportions).',
  'sign-test':
    'A non-parametric test for the median difference between paired observations. No normality assumption is needed.',
};

// ---------------------------------------------------------------------------
// Hypothesis templates per test type
// ---------------------------------------------------------------------------

export interface HypothesisDisplay {
  readonly h0: string;
  readonly h1: string;
}

type TailTypeParam = 'left' | 'two' | 'right';

export function getHypotheses(testType: StatTestType, tailType: TailTypeParam, nullMean: number): HypothesisDisplay {
  switch (testType) {
    case 'one-sample-t': {
      const op = tailType === 'two' ? '\\neq' : tailType === 'right' ? '>' : '<';
      return { h0: `$\\mu = ${nullMean}$`, h1: `$\\mu ${op} ${nullMean}$` };
    }
    case 'two-sample-t': {
      const op = tailType === 'two' ? '\\neq' : tailType === 'right' ? '>' : '<';
      return { h0: '$\\mu_1 = \\mu_2$', h1: `$\\mu_1 ${op} \\mu_2$` };
    }
    case 'paired-t': {
      const op = tailType === 'two' ? '\\neq' : tailType === 'right' ? '>' : '<';
      return { h0: '$\\mu_d = 0$', h1: `$\\mu_d ${op} 0$` };
    }
    case 'chi-squared-independence':
      return { h0: '$\\text{Variables are independent}$', h1: '$\\text{Variables are not independent}$' };
    case 'chi-squared-gof':
      return { h0: '$\\text{Data follows the expected distribution}$', h1: '$\\text{Data does not follow}$' };
    case 'sign-test': {
      const op = tailType === 'two' ? '\\neq' : tailType === 'right' ? '>' : '<';
      return { h0: '$\\text{median diff} = 0$', h1: `$\\text{median diff} ${op} 0$` };
    }
  }
}
